import styled, { css, createGlobalStyle, keyframes } from "styled-components";

const PRIMARY = "#01AB7D";
const PRIMARY_SOFT = "#B3E6D8";
const BORDER = "#E7E8EC";
const TEXT_DARK = "#131313";
const TEXT_MUTED = "#5E5E60";
const SURFACE = "#FFFFFF";
const SURFACE_ALT = "#F4F5F9";

export const PageGlobalStyle = createGlobalStyle`
  html, body, #root {
    background: ${SURFACE} !important;
    font-family: 'Noto Sans', sans-serif;
    color: ${TEXT_DARK};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

export const Page = styled.div`
  min-height: 100vh;
  background: ${SURFACE};
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid ${BORDER};
  padding: 16px 32px;
  background: ${SURFACE};

  img {
    height: 28px;
  }

  @media (max-width: 575px) {
    padding: 12px 16px;
    img { height: 24px; }
    p { font-size: 11px; }
  }

  @media (max-width: 360px) {
    padding: 10px 12px;
    img { height: 22px; }
    p { font-size: 10px; }
  }
`;

export const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px 24px 64px;
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 32px;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 575px) {
    padding: 20px 16px 48px;
    gap: 20px;
  }

  @media (max-width: 360px) {
    padding: 16px 12px 40px;
  }
`;

export const Heading = styled.div`
  margin-bottom: 24px;

  h1 {
    font-size: 22px;
    font-weight: 700;
    color: ${TEXT_DARK};
    margin: 0 0 6px;
  }
  p {
    color: ${TEXT_MUTED};
    margin: 0;
    font-size: 14px;
  }

  @media (max-width: 575px) {
    margin-bottom: 18px;
    h1 { font-size: 20px; }
    p { font-size: 13px; }
  }
`;

export const SectionCard = styled.section`
  border: 1px solid ${BORDER};
  border-radius: 7px;
  padding: 24px;
  background: ${SURFACE};
  margin-bottom: 16px;

  @media (max-width: 575px) {
    padding: 18px 16px;
  }

  @media (max-width: 360px) {
    padding: 16px 12px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  h2 {
    font-size: 15px;
    font-weight: 600;
    color: ${TEXT_DARK};
    margin: 0;
  }
`;

export const StepBadge = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  ${({ active }) =>
    active
      ? css`
          background: ${PRIMARY};
          color: #fff;
        `
      : css`
          background: #E5E7EB;
          color: #9CA3AF;
        `}
`;

export const CollapsedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .info {
    min-width: 0;
  }
  .title-row {
    display: flex;
    align-items: center;
    gap: 12px;

    h2 {
      font-size: 15px;
      font-weight: 600;
      color: ${TEXT_DARK};
      margin: 0;
    }
  }
  .summary {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0 0 38px;
    font-size: 13px;
    color: ${TEXT_MUTED};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    img {
      height: 16px;
      flex-shrink: 0;
    }
  }
`;

export const ChangeButton = styled.button`
  border: 1px solid ${BORDER};
  background: ${SURFACE};
  border-radius: 5px;
  padding: 9px 28px;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT_DARK};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover { background: ${SURFACE_ALT}; }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ cols = 2 }) => `repeat(${cols}, minmax(0, 1fr))`};
  gap: 16px;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

export const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    height: 44,
    borderRadius: 5,
    borderColor: state.isFocused ? PRIMARY : BORDER,
    boxShadow: state.isFocused ? `0 0 0 3px ${PRIMARY_SOFT}` : "none",
    backgroundColor: state.isDisabled ? SURFACE_ALT : SURFACE,
    "&:hover": { borderColor: state.isFocused ? PRIMARY : BORDER },
  }),
  valueContainer: provided => ({ ...provided, padding: "0 10px", height: 44 }),
  input: provided => ({ ...provided, margin: 0, padding: 0, color: TEXT_DARK }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? TEXT_MUTED : TEXT_DARK,
    fontSize: 14,
  }),
  placeholder: provided => ({ ...provided, color: TEXT_MUTED, fontSize: 14 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: TEXT_MUTED,
    padding: 8,
  }),
  menu: provided => ({
    ...provided,
    borderRadius: 5,
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    zIndex: 20,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#E4F6EF"
      : state.isFocused
        ? "#E9F8F1"
        : SURFACE,
    color: TEXT_DARK,
    fontSize: 14,
  }),
};

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    color: ${TEXT_DARK};
    font-weight: 500;
    margin: 0;
  }

  .req { color: #F64E60; margin-left: 2px; }

  input,
  .form-control {
    height: 44px;
    border-radius: 5px;
    border: 1px solid ${BORDER} !important;
    padding: 0 12px;
    font-size: 14px;
    color: ${TEXT_DARK};
    background: ${SURFACE};

    &:focus {
      outline: none;
      border-color: ${PRIMARY} !important;
      box-shadow: 0 0 0 3px ${PRIMARY_SOFT};
    }
    &.is-invalid {
      border-color: #F64E60 !important;
    }
    &.is-invalid:focus {
      border-color: #F64E60 !important;
      box-shadow: 0 0 0 3px rgba(246, 78, 96, 0.18);
    }
  }
  .err {
    color: #F64E60;
    font-size: 12px;
    margin: 0px;
    a {
      color: ${PRIMARY};
      font-weight: 600;
      text-decoration: underline;
    }
    a:hover { color: ${PRIMARY}; opacity: 0.85; }
  }
  .hint {
    color: ${TEXT_MUTED};
    font-size: 12px;
    margin: 0px;
  }
`;

export const InputAdornment = styled.div`
  position: relative;

  img {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    height: 22px;
  }
`;

export const Summary = styled.aside`
  background: ${SURFACE_ALT};
  border-radius: 7px;
  padding: 16px;
  position: sticky;
  top: 16px;
  align-self: start;
`;

export const PlanSelectionGroup = styled.div`
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: 9px;
  padding: 14px 12px 12px;
  margin-bottom: 12px;
`;

export const PlanSelectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 0 2px;

  .title {
    font-size: 12px;
    font-weight: 600;
    color: ${TEXT_DARK};
    letter-spacing: 0.2px;
  }
  .subtitle {
    font-size: 11px;
    color: ${TEXT_MUTED};
    font-weight: 500;
  }
`;

export const PeriodTabs = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 12px;
  align-items: end;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-2px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const FadeIn = styled.div`
  animation: ${fadeIn} 180ms ease-out;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const CepFieldWrap = styled.div`
  position: relative;

  input {
    padding-right: ${({ $loading }) => ($loading ? "36px" : undefined)};
  }
`;

export const CepSpinner = styled.span`
  position: absolute;
  right: 12px;
  top: calc(50% - 8px);
  width: 16px;
  height: 16px;
  border: 2px solid #D1D5DB;
  border-top-color: ${PRIMARY};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  transform-origin: 50% 50%;
  pointer-events: none;
`;

export const IdentificationLoadingBody = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 8px 0;

  ${CepSpinner} {
    position: static;
    top: auto;
    right: auto;
  }

  .hint {
    font-size: 13px;
    color: ${TEXT_MUTED};
  }
`;

export const PeriodTabWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const SaveBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #B3E6D8;
  color: #017858;
  font-size: 11px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 5px 5px 0 0;
  white-space: nowrap;
`;

export const PeriodTab = styled.button`
  position: relative;
  width: 100%;
  text-align: left;
  padding: ${({ compact }) =>
    compact ? "10px 36px 10px 12px" : "12px 38px 12px 14px"};
  border-radius: ${({ withBadge }) => (withBadge ? "0 0 7px 7px" : "7px")};
  border: 1.5px solid ${({ active }) => (active ? PRIMARY : BORDER)};
  ${({ withBadge }) => withBadge && "border-top: 0;"}
  background: ${SURFACE};
  cursor: pointer;
  font-size: 13px;
  color: ${TEXT_MUTED};
  transition: border-color 160ms ease, background 160ms ease;
  overflow: hidden;

  .tab-label {
    display: block;
    font-weight: 600;
    font-size: 13px;
    color: ${({ active }) => (active ? PRIMARY : TEXT_MUTED)};
  }

  small {
    display: block;
    color: ${TEXT_MUTED};
    font-size: 12px;
    margin-top: 2px;
  }

  .tab-price {
    display: block;
    margin-top: 3px;
    font-size: 12px;
    font-weight: 400;
    color: ${TEXT_DARK};
    line-height: 1.25;
    white-space: nowrap;

    @media (max-width: 768px) {
      white-space: normal;
    }
  }
  .tab-period {
    font-weight: 500;
    color: ${TEXT_MUTED};
    margin-left: 1px;
  }
  .tab-equiv {
    font-weight: 500;
    color: #838486;
    font-size: 11px;
    line-height: 1.25;
    margin-left: 4px;
    white-space: nowrap;

    @media (max-width: 768px) {
      display: block;
      margin-left: 0;
      margin-top: 2px;
    }
  }

  .radio {
    position: absolute;
    right: 10px;
    top: 12px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${({ active }) => (active ? PRIMARY : "#C4C7D1")};
    background: ${({ active }) =>
      active
        ? `radial-gradient(circle, ${PRIMARY} 45%, #fff 50%)`
        : "#fff"};
    transition: border-color 160ms ease, background 160ms ease;
  }
`;

export const Card = styled.div`
  background: ${SURFACE};
  border-radius: 7px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid ${BORDER};
`;

export const PlanCards = styled.div`
  display: grid;
  grid-template-columns: ${({ count }) =>
    count === 1 ? '1fr' :
    count === 2 ? '1fr 1fr' :
    count >= 4  ? '1fr 1fr' :
    '1fr 1fr 1fr'};
  gap: 8px;
  margin-top: 14px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const PlanCard = styled.button`
  position: relative;
  padding: 10px 10px 10px 12px;
  border-radius: 8px;
  min-width: 0;
  border: 1.5px solid ${({ active }) => (active ? PRIMARY : BORDER)};
  background: ${SURFACE};
  cursor: pointer;
  text-align: left;
  color: ${TEXT_MUTED};
  transition: border-color 180ms ease, transform 120ms ease;

  &:hover {
    border-color: ${({ active }) => (active ? PRIMARY : "#9FBEB4")};
  }

  &:active {
    transform: scale(0.98);
  }

  .plan-name {
    font-size: 13px;
    font-weight: 700;
    color: ${({ active }) => (active ? PRIMARY : TEXT_MUTED)};
    text-transform: capitalize;
    letter-spacing: 0.1px;
    padding-right: 22px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .plan-tag {
    margin-top: 2px;
    font-size: 10px;
    color: ${TEXT_MUTED};
    line-height: 1.3;
    font-weight: 500;
  }
  .plan-price {
    margin-top: 4px;
    font-size: 12px;
    font-weight: 400;
    color: ${TEXT_MUTED};
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .plan-period {
    font-size: 11px;
    color: ${TEXT_MUTED};
    font-weight: 500;
    margin-left: 1px;
  }
  .plan-equiv {
    margin-top: 4px;
    font-size: 10px;
    color: ${TEXT_MUTED};
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .plan-radio {
    position: absolute;
    top: 12px;
    right: 10px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${({ active }) => (active ? PRIMARY : "#C4C7D1")};
    background: ${({ active }) =>
      active
        ? `radial-gradient(circle, ${PRIMARY} 45%, #fff 50%)`
        : "#fff"};
    transition: border-color 160ms ease, background 160ms ease;
  }
`;

export const PlanInfoCard = styled(Card)`
  .top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }
  .top > div:first-child {
    min-width: 0;
    flex: 1;
  }
  .field-label {
    font-size: 12px;
    color: ${TEXT_MUTED};
    margin: 0 0 2px;
    font-weight: 500;
  }
  .name {
    font-size: 15px;
    font-weight: 700;
    color: ${TEXT_DARK};
    margin: 0;
  }
  .desc {
    font-size: 12px;
    color: ${TEXT_MUTED};
    margin: 10px 0 0;
    line-height: 1.5;
  }

  @media (max-width: 360px) {
    .top { flex-direction: column; align-items: stretch; }
  }
`;

export const SmallBtn = styled.button`
  border: 1px solid ${BORDER};
  background: ${SURFACE_ALT};
  border-radius: 5px;
  padding: 6px 12px;
  font-size: 12px;
  color: ${TEXT_DARK};
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: ${SURFACE_ALT}; }
`;

export const PurchaseCard = styled(Card)`
  padding: 16px 16px 14px;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
  }
  .head h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: ${TEXT_DARK};
  }

  .section-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${TEXT_DARK};
    margin-bottom: 8px;

    .bullet {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${PRIMARY};
      display: inline-block;
    }
  }

  .line-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 4px 0 4px 16px;
    font-size: 13px;
    color: ${TEXT_DARK};

    .line-name {
      color: ${TEXT_MUTED};
    }
    .line-name.code {
      font-weight: 600;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }
    .line-value {
      color: ${TEXT_DARK};
      font-size: 13px;
      white-space: nowrap;
    }
    .line-value.muted {
      color: ${TEXT_MUTED};
    }
    .line-value.strike {
      text-decoration: line-through;
      font-size: 12px;
    }
    .line-value.discount {
      color: ${PRIMARY};
      font-weight: 600;
    }
    .line-value.bold {
      font-weight: 700;
    }
  }

  .divider {
    height: 1px;
    background: ${BORDER};
    margin: 10px 0;
  }

  .second-month {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${BORDER};

    .left {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .left .title {
      font-size: 13px;
      color: ${TEXT_DARK};
    }
    .left .date {
      font-size: 11px;
      color: ${TEXT_MUTED};
    }
    .right {
      font-size: 13px;
      color: ${TEXT_DARK};
      font-weight: 600;
      white-space: nowrap;
    }
  }
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  color: ${TEXT_DARK};
  padding: 4px 0;

  .strike {
    text-decoration: line-through;
    color: ${TEXT_MUTED};
    font-size: 12px;
  }
  .discount {
    color: ${PRIMARY};
    font-weight: 600;
  }
  .row-label {
    color: ${TEXT_MUTED};
  }
  &.bold {
    font-weight: 700;
    color: ${TEXT_DARK};
  }
`;

export const NextMonthBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${SURFACE_ALT};
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 10px;

  .left .title { font-size: 13px; color: ${TEXT_DARK}; }
  .left .sub { font-size: 11px; color: ${TEXT_MUTED}; }
  .right { font-size: 13px; font-weight: 600; color: ${TEXT_DARK}; }
`;

export const CouponExpansion = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${BORDER};

  .head {
    width: 100%;
    background: transparent;
    border: 0;
    padding: 0;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-size: 13px;
    color: ${TEXT_DARK};
    font-weight: 500;
  }
  .head:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
    border-radius: 4px;
  }
  .head .chev {
    width: 28px;
    height: 28px;
    border-radius: 5px;
    background: ${SURFACE_ALT};
    border: 1px solid ${BORDER};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${TEXT_MUTED};
    transition: transform 0.2s ease;
  }
  .head .chev.open { transform: rotate(180deg); }
  .head .chev svg { font-size: 18px; }
  .body { margin-top: 10px; display: flex; gap: 8px; }
  .body input {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    border: 1px solid ${BORDER};
    padding: 0 10px;
    font-size: 13px;
    text-transform: uppercase;
  }
  .body button {
    height: 40px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: ${PRIMARY};
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .feedback { font-size: 12px; margin-top: 6px; }
  .feedback.ok { color: ${PRIMARY}; }
  .feedback.err { color: #F64E60; }
  .applied {
    margin-top: 10px;
    display: flex;
    gap: 8px;
    align-items: stretch;
  }
  .applied .field {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    border: 1px solid ${BORDER};
    border-radius: 8px;
    padding: 0 10px;
    background: #fff;
  }
  .applied .field input {
    flex: 1;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: ${TEXT_DARK};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 0;
  }
  .applied.invalid .field {
    border-color: #F8C7CB;
    background: #FDF4F5;
  }
  .applied.invalid .field input { color: #F64E60; }
  .applied .remove-btn {
    height: 40px;
    padding: 0 14px;
    border: 1px solid ${PRIMARY};
    border-radius: 8px;
    background: #fff;
    color: ${PRIMARY};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .applied .remove-btn:hover { background: #E4F6EF; }
  .applied.invalid .remove-btn {
    border-color: #F64E60;
    color: #F64E60;
  }
  .applied.invalid .remove-btn:hover { background: #FDECEE; }
`;

export const Total = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 4px 8px;
  font-size: 14px;
  color: ${TEXT_DARK};

  strong {
    font-size: 20px;
    font-weight: 700;
  }
`;

export const TermsAcceptance = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${TEXT_DARK};
  opacity: 0.7;
  text-align: center;

  a {
    color: ${PRIMARY};
    font-weight: 600;
    text-decoration: underline;
  }
  a:hover { color: ${PRIMARY}; opacity: 0.85; }
`;

export const PrimaryButton = styled.button`
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 5px;
  background: ${PRIMARY};
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Footnote = styled.p`
  text-align: center;
  font-size: 12px;
  color: ${TEXT_MUTED};
  margin: 0;
`;

export const Disclosure = styled.button`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border: 0;
  padding: 4px 0;
  color: ${TEXT_DARK};
  font-size: 13px;
  cursor: pointer;
`;

export const CompleteCheck = styled.span`
  margin-left: auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${PRIMARY};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
`;

export const LinkButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${PRIMARY};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 19, 36, 0.55);
  display: ${({ open }) => (open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 575px) {
    padding: 12px;
  }
`;

export const ModalDialog = styled.div`
  position: relative;
  background: ${SURFACE};
  border-radius: 12px;
  width: 100%;
  max-width: 820px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(15, 19, 36, 0.18);

  @media (max-width: 720px) {
    max-width: 460px;
  }
`;

export const ModalContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

export const ModalMain = styled.div`
  flex: 1 1 460px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const ModalAside = styled.aside`
  flex: 1 1 320px;
  min-width: 0;
  background: linear-gradient(160deg, #0F2A23 0%, #0B3A2E 55%, #01AB7D 140%);
  border-left: 1px solid ${BORDER};
  color: #FFFFFF;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
        circle at 85% 15%,
        rgba(255, 255, 255, 0.08) 0,
        rgba(255, 255, 255, 0) 45%
      ),
      radial-gradient(
        circle at 15% 90%,
        rgba(1, 171, 125, 0.35) 0,
        rgba(1, 171, 125, 0) 55%
      );
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  .aside-logo {
    height: 28px;
    width: auto;
    filter: brightness(0) invert(1);
    align-self: flex-start;
  }
  .aside-headline {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
    color: #FFFFFF;
  }
  .aside-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    margin: 0;
    line-height: 1.5;
  }
  .aside-list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .aside-list li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 13px;
    line-height: 1.4;
    color: #FFFFFF;
  }
  .aside-list svg {
    flex-shrink: 0;
    color: ${PRIMARY};
    background: rgba(255, 255, 255, 0.95);
    border-radius: 50%;
    font-size: 18px;
    margin-top: 1px;
  }
  .aside-trust {
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 11px;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.5;
  }

  @media (max-width: 720px) {
    display: none;
  }
`;

export const ModalBody = styled.div`
  padding: 24px 24px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .modal-title {
    font-size: 18px;
    font-weight: 700;
    color: ${TEXT_DARK};
    margin: 0;
  }
  .plan-name {
    font-size: 15px;
    font-weight: 700;
    color: ${TEXT_DARK};
    margin: 4px 0 0;
  }
  .desc {
    color: ${TEXT_MUTED};
    font-size: 13px;
    margin: 0;
    line-height: 1.5;
  }
  .price {
    font-size: 22px;
    font-weight: 700;
    color: ${TEXT_DARK};
    margin: 4px 0 0;
  }
  .price small {
    font-size: 13px;
    font-weight: 500;
    color: ${TEXT_MUTED};
  }
  .price-old {
    color: ${TEXT_MUTED};
    text-decoration: line-through;
    font-size: 13px;
    margin: 0;
  }
  .features-label {
    font-size: 13px;
    color: ${TEXT_DARK};
    font-weight: 500;
    margin: 6px 0 0;
  }

  @media (max-width: 575px) {
    padding: 20px 16px 16px;
    .modal-title { font-size: 17px; }
    .price { font-size: 20px; }
  }
`;

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    font-size: 13px;
    color: ${TEXT_DARK};
    display: flex;
    gap: 8px;
    align-items: flex-start;
    line-height: 1.4;

    svg {
      color: ${PRIMARY};
      font-size: 16px;
      flex-shrink: 0;
      margin-top: 1px;
    }
  }
`;

export const ModalClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  color: ${TEXT_MUTED};
  z-index: 2;

  &:hover { background: ${SURFACE_ALT}; }
`;

export const PeriodRadios = styled.div`
  display: flex;
  gap: 18px;
  margin: 2px 0 4px;
`;

export const PeriodRadio = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${TEXT_DARK};
  cursor: pointer;

  input {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${({ checked }) => (checked ? PRIMARY : "#C4C7D1")};
    background: ${({ checked }) =>
      checked
        ? `radial-gradient(circle, ${PRIMARY} 45%, #fff 50%)`
        : "#fff"};
    margin: 0;
    cursor: pointer;
  }
`;

export const ModalFooter = styled.div`
  padding: 16px 24px 20px;
  border-top: 1px solid ${BORDER};
  background: ${SURFACE};

  @media (max-width: 575px) {
    padding: 14px 16px 16px;
  }
`;

export const CupomReferenceFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;

  label {
    display: block;
    font-size: 12px;
    color: ${TEXT_DARK};
    font-weight: 500;
    margin-bottom: 4px;

    .req { color: #E04444; }
  }
  input.form-control {
    width: 100%;
    height: 36px;
    border-radius: 6px;
    border: 1px solid ${BORDER};
    padding: 0 10px;
    font-size: 13px;
    color: ${TEXT_DARK};
    background: #fff;

    &.is-invalid { border-color: #E04444; }
    &:focus { outline: none; border-color: ${PRIMARY}; }
  }
  .err {
    color: #E04444;
    font-size: 11px;
    margin-top: 4px;
    display: block;
  }
`;

export const CompraGarantida = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(1, 171, 125, 0.08);
  border: 1px solid rgba(1, 171, 125, 0.25);
  color: ${TEXT_DARK};
  font-size: 12px;
  line-height: 1.45;
  margin-bottom: 10px;

  svg { color: ${PRIMARY}; flex-shrink: 0; margin-top: 1px; }
  strong { color: ${TEXT_DARK}; font-weight: 700; }
`;

export const SecondaryButton = styled.button`
  height: 44px;
  padding: 0 18px;
  border: 1px solid ${BORDER};
  border-radius: 5px;
  background: #fff;
  color: ${TEXT_DARK};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:hover { background: ${SURFACE_ALT}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const EmailExistsDialog = styled.div`
  position: relative;
  background: ${SURFACE};
  border-radius: 8px;
  width: 100%;
  max-width: 460px;
  padding: 32px 28px 28px;
  box-shadow: 0 12px 40px rgba(15, 19, 36, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;

  .icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #FBEAEC;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
      font-size: 30px;
      color: #E04444;
    }
  }
  h3 {
    font-size: 18px;
    font-weight: 700;
    color: ${TEXT_DARK};
    margin: 4px 0 0;
  }
  p {
    font-size: 14px;
    color: ${TEXT_MUTED};
    line-height: 1.5;
    margin: 0;

    strong { color: ${TEXT_DARK}; font-weight: 600; }
  }
  .actions {
    display: flex;
    gap: 12px;
    margin-top: 12px;
    width: 100%;
    justify-content: center;

    > button {
      flex: 0 1 auto;
      width: auto;
      min-width: 160px;
      height: 44px;
      padding: 0 18px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    padding: 24px 20px 20px;

    .actions {
      flex-direction: column;
      gap: 10px;

      > button {
        width: 100%;
        min-width: 0;
      }
    }
  }
`;

